import React, { Suspense, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { FiAlertCircle } from '@react-icons/all-files/fi/FiAlertCircle'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCheckCircle } from '@react-icons/all-files/fi/FiCheckCircle'
import { FiChevronDown } from '@react-icons/all-files/fi/FiChevronDown'
import { FiChevronUp } from '@react-icons/all-files/fi/FiChevronUp'
import { FiDisc } from '@react-icons/all-files/fi/FiDisc'
import { FiFilter } from '@react-icons/all-files/fi/FiFilter'
import { FiGrid } from '@react-icons/all-files/fi/FiGrid'
import { FiInfo } from '@react-icons/all-files/fi/FiInfo'
import { FiLayers } from '@react-icons/all-files/fi/FiLayers'
import { FiList } from '@react-icons/all-files/fi/FiList'
import { FiMonitor } from '@react-icons/all-files/fi/FiMonitor'
import { FiPackage } from '@react-icons/all-files/fi/FiPackage'
import { FiPlusCircle } from '@react-icons/all-files/fi/FiPlusCircle'
import { FiSearch } from '@react-icons/all-files/fi/FiSearch'
import { FiShoppingCart } from '@react-icons/all-files/fi/FiShoppingCart'
import { FiSliders } from '@react-icons/all-files/fi/FiSliders'
import { FiSun } from '@react-icons/all-files/fi/FiSun'
import { FiTag } from '@react-icons/all-files/fi/FiTag'
import { FiTruck } from '@react-icons/all-files/fi/FiTruck'
import { FiVolume2 } from '@react-icons/all-files/fi/FiVolume2'
import { FiX } from '@react-icons/all-files/fi/FiX'
import { FiZap } from '@react-icons/all-files/fi/FiZap'
import AddUnits from '../../AddUnits'
import { Spinner } from '../../Components'
import { useOrderFlow } from '../../unified-order/OrderFlowProvider'
import { helper } from '../../../utilities/helper'
import { resolveOrderProduct } from '../../../utilities/orderProductHydration'
import {
  getProductionBuilderCategory,
  getProductionEquipmentCategory,
  PRODUCTION_EQUIPMENT_CATEGORIES
} from '../../../utilities/productionEquipmentCategories'
import { getAllFilteredProducts, getProductSlice, getProductsFilters, productsResetFilters, productsSetFilters } from '../../../store/slices/entities/products'
import { cartAddItem, cartCleaned, getUser, getUserGeo, purchaseAddItem } from '../../../store/slices/user'
import { finalizeRentalDraft, getWorkingOrder, getWorkingOrderSettings, workingOrderReset, workingOrderSetSettings } from '../../../store/slices/entities/orders'
import { getWorkingPackage, getWorkingPackagePersistencePending, packageViewSet } from '../../../store/slices/entities/packages'
import { CLOSE_POPUP, openSideMenu, showAlert, SHOW_POPUP } from '../../../store/slices/ui'
import { apiCallBegan } from '../../../store/api'
import { loadEntityForce } from '../../../store/slices/entities/crmActions'
import { requiresMiamiFulfillment, resolveShoppingMarket } from '../../../utilities/fulfillmentMarket'
import * as gtag from '../../../lib/gtag'
import UiBoundary from '../foundations/UiBoundary'
import useDialogFocus from '../foundations/useDialogFocus'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import RentalStartPopup from './RentalStartPopup'
import styles from './ProductionEquipmentSelector.module.scss'

const StageBuilder = dynamic(() => import('../../StageBuilder'), { suspense: true })
const VideoWallBuilder = dynamic(() => import('../../VideoWallBuilder'), { suspense: true })

const categoryIcons = {
  Sound: FiVolume2,
  Staging: FiLayers,
  Visuals: FiMonitor,
  'DJ Equipment': FiDisc,
  Lighting: FiSun,
  'Special Effects': FiZap,
  Other: FiPackage
}

const normalize = value => String(value || '').trim().toLowerCase()
const tag = value => helper.tagLower(String(value || ''))

const parseSnippet = snippet => {
  const [label, value] = String(snippet || '').split('//')
  return label && value ? { label, value } : null
}

const productMatchesCategory = (product, category) => (
  !category || getProductionEquipmentCategory(product) === category
)

const productMatchesSubcategories = (product, selectedSubcategories) => {
  if (!selectedSubcategories.length) return true

  return (product?.categories || []).some(path => {
    const segments = String(path).split('/').map(tag)
    return selectedSubcategories.some(subcategory => segments.includes(subcategory))
  })
}

const getBuilderInformation = builder => {
  if(builder?.configuration?.kind === 'pipe_drape') return {title:'Pipe & Drape',dimensions:`${builder.configuration.drape.height}′ high · ${builder.configuration.drape.sections.length} sections`,wall:false}
  const [type, family] = String(builder?.type || '').split('//')
  const [length, width, height] = String(builder?.vars || '').split('//')
  const wall = type === 'wall'
  const familyNames = { int: 'Intellistage', msr: 'MSR', lumo: 'Lumo' }
  const title = `Custom ${familyNames[family] || ''} ${wall ? 'Video Wall' : 'Stage'}`.replace(/\s+/g, ' ').trim()
  const dimensions = wall
    ? `${width || '—'}m W × ${height || '—'}m H`
    : `${length || '—'}' L × ${width || '—'}' W × ${height === '8' ? '9' : (height || '—')}\" H`

  return { title, dimensions, wall }
}

const Availability = ({ state, orlando }) => {
  const atOrlando = orlando ?? state.orlando

  if (atOrlando && state.shipsFromMiami) {
    return <span className={`${styles.availability} ${styles.shipping}`}><FiTruck aria-hidden='true' />Ships from Miami</span>
  }

  if (!state.checked) {
    return <span className={styles.availability}><FiCheckCircle aria-hidden='true' />Dates checked before adding</span>
  }

  if (state.unavailable) {
    return <span className={`${styles.availability} ${styles.unavailable}`}><FiAlertCircle aria-hidden='true' />Unavailable for these dates</span>
  }

  if (state.low) {
    return <span className={`${styles.availability} ${styles.low}`}><FiAlertCircle aria-hidden='true' />Only {state.count} available</span>
  }

  return <span className={`${styles.availability} ${styles.available}`}><FiCheckCircle aria-hidden='true' />Available for your event</span>
}

const ProductDetailsDialog = ({ product, pack, selectedUnits, availability, contextName = 'setup', deliveryOnly = false, locked, onAdd, onClose, publicActions, resolveProduct }) => {
  const closeRef = useRef(null)
  const dialogRef = useRef(null)
  const titleId = useId()
  const included = (product?.included || []).map(item => ({
    ...item,
    product: resolveProduct(item?.product)
  })).filter(item => item.product)

  useDialogFocus({ dialogRef, initialFocusRef: closeRef, onClose, open: Boolean(product) })

  if (!product) return null

  return <>
    <button aria-hidden='true' className={styles.scrim} data-dialog-backdrop onClick={onClose} tabIndex={-1} type='button' />
    <section aria-labelledby={titleId} aria-modal='true' className={styles.detailsDialog} ref={dialogRef} role='dialog' tabIndex={-1}>
      <header className={styles.dialogHeader}>
        <div>
          <span className={styles.eyebrow}>Equipment details</span>
          <h2 id={titleId}>{product.name}</h2>
        </div>
        <button aria-label='Close equipment details' className={styles.close} onClick={onClose} ref={closeRef} type='button'>
          <FiX aria-hidden='true' />
        </button>
      </header>
      <div className={styles.detailsBody}>
        <Media alt={product.name} className={styles.detailsMedia} picture={product.picture} ratio='1 / 1' sizes='(max-width: 599px) 88vw, 360px' />
        <div className={styles.detailsInformation}>
          <Availability state={availability} orlando={availability.orlando} />
          {deliveryOnly && <span className={styles.deliveryOnly}><FiTruck aria-hidden='true' />Delivery &amp; setup required</span>}
          <p>{product.description || 'Ask our team if you need help confirming whether this item fits your setup.'}</p>
          {product.snippets?.length > 0 && <dl className={styles.specificationList}>
            {product.snippets.slice(0, 6).map(snippet => {
              const parsed = parseSnippet(snippet)
              if (!parsed) return null
              return <div key={snippet}><dt>{parsed.label}</dt><dd>{parsed.value}</dd></div>
            })}
          </dl>}
          {included.length > 0 && <section className={styles.included} aria-label={`Included with ${product.name}`}>
            <h3>Included at no extra charge</h3>
            <ul>
              {included.map((item, index) => <li key={`${item.product?._id || index}-${index}`}>
                <span>{Number(item.units) || 1} × {item.product.name}</span>
              </li>)}
            </ul>
          </section>}
          <div className={styles.detailsPurchase}>
            <div><span>Rental price</span><strong>{helper.formatPrice(product?.price?.rental || 0)} / day</strong></div>
            {publicActions || <AddUnits
                actionIcon={deliveryOnly ? FiTruck : undefined}
                actionLabel={deliveryOnly ? 'Add with delivery' : undefined}
                actionVariant={deliveryOnly ? 'outline' : 'primary'}
                busy={locked}
                contextName={contextName}
                disabled={availability.unavailable}
                disabledLabel='Unavailable for these dates'
                onAdd={onAdd}
                pack={pack}
                product={product}
                selectedUnits={selectedUnits}
                ui='v2'
              />}
          </div>
        </div>
      </div>
    </section>
  </>
}

const SetupDrawer = ({ open, pack, resolveProduct, getAvailability, isPhone, locked, onAdd, onClose, onReview }) => {
  const closeRef = useRef(null)
  const drawerRef = useRef(null)
  const titleId = useId()
  const [openBuilders, setOpenBuilders] = useState({})
  const [drawerQuery, setDrawerQuery] = useState('')
  const standaloneComponents = (pack?.components || []).filter(component => component?.builder_index == null)
  const normalizedDrawerQuery = normalize(drawerQuery)
  const groups = PRODUCTION_EQUIPMENT_CATEGORIES.map(category => ({
    category,
    components: standaloneComponents
      .map(component => ({ ...component, product: resolveProduct(component.product) }))
      .filter(component => component.product
        && getProductionEquipmentCategory(component.product) === category
        && (!normalizedDrawerQuery || normalize(component.product.name).includes(normalizedDrawerQuery)))
  })).filter(group => group.components.length)
  const itemCount = standaloneComponents.reduce((total, component) => total + (Number(component?.units) || 0), 0)
  const configuredSystemCount = (pack?.builders || []).length
  const drawerCountLabel = [
    itemCount > 0 ? `${itemCount} equipment ${itemCount === 1 ? 'unit' : 'units'}` : '',
    configuredSystemCount > 0 ? `${configuredSystemCount} configured ${configuredSystemCount === 1 ? 'system' : 'systems'}` : '',
  ].filter(Boolean).join(' · ') || 'No equipment added yet'
  const rentalSubtotal = Number(pack?.price) || 0
  const purchaseSubtotal = Number(pack?.purchase_price) || 0
  const labor = (Number(pack?.production_price) || 0) * 1.35
  const estimatedSubtotal = rentalSubtotal + purchaseSubtotal + labor

  useDialogFocus({ dialogRef: drawerRef, initialFocusRef: closeRef, onClose, open })

  useEffect(() => {
    if (!open) setDrawerQuery('')
  }, [open])

  if (!open) return null

  return <>
    <button aria-hidden='true' className={styles.scrim} data-dialog-backdrop onClick={onClose} tabIndex={-1} type='button' />
    <aside aria-labelledby={titleId} aria-modal='true' className={styles.drawer} id='production-setup-drawer' ref={drawerRef} role='dialog' tabIndex={-1}>
      <header className={styles.drawerHeader}>
        <div>
          <span className={styles.eyebrow}>Current setup</span>
          <h2 id={titleId}>{pack?.name || 'Your setup'}</h2>
          <p>{drawerCountLabel}</p>
        </div>
        <div className={styles.drawerHeaderActions}>
          <button className={styles.mobileDrawerBack} onClick={onClose} type='button'>Back to results</button>
          <button aria-label='Close setup summary' className={styles.close} onClick={onClose} ref={closeRef} type='button'><FiX aria-hidden='true' /></button>
        </div>
      </header>

      <div className={styles.drawerScroll}>
        <label className={styles.drawerSearch}>
          <span>Search this setup</span>
          <span className={styles.drawerSearchControl}><FiSearch aria-hidden='true' /><input onChange={event => setDrawerQuery(event.target.value)} placeholder='Find an item in this setup' type='search' value={drawerQuery} />{drawerQuery && <button aria-label='Clear setup search' onClick={() => setDrawerQuery('')} type='button'><FiX aria-hidden='true' /></button>}</span>
        </label>
        {(pack?.builders || []).length > 0 && <section className={styles.drawerSection} aria-labelledby='configured-systems-title'>
          <div className={styles.drawerSectionHeader}>
            <h3 id='configured-systems-title'>Configured systems</h3>
            <span>{pack.builders.length}</span>
          </div>
          {pack.builders.map((builder, index) => {
            const builderIndex = Number(String(builder.type || '').split('//')[2])
            const components = (pack.components || []).filter(component => component.builder_index === builderIndex)
            const information = getBuilderInformation(builder)
            const price = components.reduce((total, component) => {
              const product = resolveProduct(component.product)
              return total + ((Number(product?.price?.rental) || 0) * (Number(component.units) || 0))
            }, 0)
            const isOpen = Boolean(openBuilders[index])
            const CategoryIcon = categoryIcons[getProductionBuilderCategory(builder)] || FiPackage

            return <article className={styles.system} key={`${builder.type}-${index}`}>
              <div className={styles.systemSummary}>
                <span className={styles.systemIcon}><CategoryIcon aria-hidden='true' /></span>
                <div><strong>{information.title}</strong><span>{information.dimensions} · {components.length} component types</span></div>
                <div className={styles.systemPrice}><span>System total</span><strong>{helper.formatPrice(price)}</strong></div>
                <button
                  aria-controls={`setup-system-${index}`}
                  aria-expanded={isOpen}
                  aria-label={`${isOpen ? 'Hide' : 'Show'} components in ${information.title}`}
                  className={styles.disclosure}
                  onClick={() => setOpenBuilders(value => ({ ...value, [index]: !isOpen }))}
                  type='button'
                >{isOpen ? <FiChevronUp aria-hidden='true' /> : <FiChevronDown aria-hidden='true' />}</button>
              </div>
              {isOpen && <ul className={styles.systemComponents} id={`setup-system-${index}`}>
                {components.map((component, componentIndex) => {
                  const product = resolveProduct(component.product)
                  return <li key={`${product?._id || componentIndex}-${componentIndex}`}>
                    <span>{Number(component.units) || 0} × {product?.name || 'System component'}</span>
                    <strong>{helper.formatPrice((Number(product?.price?.rental) || 0) * (Number(component.units) || 0))}</strong>
                  </li>
                })}
              </ul>}
            </article>
          })}
        </section>}

        <section className={styles.drawerSection} aria-labelledby='setup-equipment-title'>
          <div className={styles.drawerSectionHeader}>
            <h3 id='setup-equipment-title'>Equipment</h3>
            <span>{standaloneComponents.length} products</span>
          </div>
          {groups.length ? groups.map(group => {
            const CategoryIcon = categoryIcons[group.category] || FiPackage
            return <div className={styles.drawerGroup} key={group.category}>
              <div className={styles.drawerGroupTitle}><CategoryIcon aria-hidden='true' /><strong>{group.category}</strong><span>{group.components.length}</span></div>
              {group.components.map((component, index) => {
                const product = component.product
                const units = Number(component.units) || 0
                const lineTotal = (Number(product?.price?.rental) || 0) * units
                const availability = getAvailability(product)
                return <article className={styles.drawerItem} key={`${product?._id || index}-${index}`}>
                  <Media alt='' className={styles.drawerMedia} picture={product.picture} ratio='1 / 1' sizes='64px' />
                  <div className={styles.drawerItemName}><strong>{product.name}</strong><span>{helper.formatPrice(product?.price?.rental || 0)} / day</span>{isPhone && availability.checked && availability.count === 0 && <small>{units} selected · maximum available for these dates</small>}</div>
                  <AddUnits busy={locked} contextName='setup' disabled={Boolean(isPhone && availability.unavailable)} onAdd={onAdd} pack={pack} product={product} selectedUnits={units} ui='v2' />
                  <div className={styles.drawerLinePrice}><span>Line total</span><strong>{helper.formatPrice(lineTotal)}</strong></div>
                </article>
              })}
            </div>
          }) : <div className={styles.emptyDrawer}><FiPackage aria-hidden='true' /><p>{drawerQuery ? 'No setup items match this search.' : 'Add equipment from the catalog to build this setup.'}</p></div>}
        </section>

        <section aria-label='Estimated setup subtotal' className={styles.drawerTotals}>
          <div><span>Equipment rental</span><strong>{helper.formatPrice(rentalSubtotal)}</strong></div>
          {purchaseSubtotal > 0 && <div><span>Purchased items</span><strong>{helper.formatPrice(purchaseSubtotal)}</strong></div>}
          <div><span>Estimated delivery &amp; labor</span><strong>{helper.formatPrice(labor)}</strong></div>
          <div className={styles.drawerGrandTotal}><span>Estimated setup subtotal</span><strong>{helper.formatPrice(estimatedSubtotal)}</strong></div>
          <p>Taxes, discounts, monitoring, and final production details are confirmed in the Package Builder and order review.</p>
        </section>
      </div>

      <footer className={styles.drawerFooter}>
        <Action className={`${styles.drawerFooterAction} ${styles.drawerBrowseAction}`} icon={FiGrid} onClick={onClose} variant='secondary'>Continue browsing</Action>
        <Action className={styles.drawerFooterAction} disabled={locked} icon={FiArrowRight} onClick={onReview}>{locked ? 'Saving setup…' : 'Review setup'}</Action>
      </footer>
    </aside>
  </>
}

const RentalCatalogActions = ({ addToCartItems, card = false, cart, locked, onAdd, openRentalStart, order, orlando, pack, product, selectedUnits, user }) => {
  const dispatch = useDispatch()
  const { pathname, push } = useRouter()
  const purchaseOrder = order?.request_type === 'purchase'
  const activeCartItem = cart?.items?.find(item => String(item?.product?._id) === String(product?._id) || item?.product?.name === product?.name)
  const pickupAvailable = product?.allow_pickup === true
  const miamiFulfillment = orlando && requiresMiamiFulfillment(product)

  const addPickup = () => {
    if (product.only_purchase && !order?._id) {
      return dispatch(purchaseAddItem({ product, units: 1, push, orlando, user, pathname }))
    }
    return addToCartItems(product)
  }

  if (pack) {
    return <AddUnits
      busy={locked}
      contextName='setup'
      onAdd={onAdd}
      pack={pack}
      product={product}
      selectedUnits={selectedUnits}
      ui='v2'
    />
  }

  if (cart?.items?.length) {
    if (purchaseOrder) {
      return <Action
        disabled={!pickupAvailable || miamiFulfillment}
        icon={FiShoppingCart}
        onClick={() => (product.only_purchase || product?.price?.map)
          ? dispatch(purchaseAddItem({ product, units: 1, push, orlando, user, pathname }))
          : addToCartItems(product)}
        size='compact'
      >
        {!pickupAvailable || miamiFulfillment
          ? 'Delivery required'
          : (product.only_purchase || product?.price?.map) ? 'Purchase product' : 'Rent product'}
      </Action>
    }

    return <div className={styles.publicActionGrid}>
      <Action
        ariaLabel={`Switch this rental to delivery and add ${product.name}`}
        icon={FiTruck}
        onClick={() => addToCartItems(product, true)}
        size='compact'
        variant='secondary'
      >
        Use delivery
      </Action>
      <AddUnits
        busy={locked}
        contextName='rental'
        product={product}
        selectedUnits={activeCartItem?.units || 0}
        ui='v2'
      />
    </div>
  }

  if (product.only_purchase) {
    return <div className={styles.publicActionGrid}>
      <Action icon={FiTruck} onClick={() => addToCartItems(product, true)} size='compact' variant='secondary'>
        {miamiFulfillment ? 'Deliver from Miami' : 'Schedule delivery'}
      </Action>
      <Action disabled={!pickupAvailable || miamiFulfillment} icon={FiShoppingCart} onClick={addPickup} size='compact'>
        {!pickupAvailable || miamiFulfillment ? 'Delivery required' : 'Purchase product'}
      </Action>
    </div>
  }

  return <div className={styles.publicEntryAction}>
    <span>{pickupAvailable ? 'Pickup or delivery · choose dates next' : 'Delivery & setup only · choose dates next'}</span>
    <Action
      ariaLabel={`Choose Dates & Rent Online for ${product.name}`}
      className={card ? styles.publicPrimaryAction : ''}
      icon={FiCalendar}
      onClick={() => openRentalStart(product)}
      size='compact'
    >
      Choose Dates &amp; Rent Online
    </Action>
  </div>
}

const ProductCard = ({ product, pack, selectedUnits, availability, contextName = 'setup', deliveryOnly = false, locked, onAdd, onDetails, publicActions }) => {
  const specifications = (product?.snippets || []).map(parseSnippet).filter(Boolean).slice(0, 3)
  const includedCount = (product?.included || []).reduce((total, item) => total + (Number(item?.units) || 1), 0)

  return <article className={styles.productCard}>
    <button aria-label={`View details for ${product.name}`} className={styles.productMediaButton} onClick={onDetails} type='button'>
      <Media alt={product.name} className={styles.productMedia} picture={product.picture} ratio='16 / 10' sizes='(max-width: 599px) 44vw, (max-width: 1199px) 27vw, 220px' />
    </button>
    <div className={styles.productCardBody}>
      <div className={styles.cardSignals}>
        <Availability state={availability} orlando={availability.orlando} />
        {deliveryOnly && <span className={styles.deliveryOnly}><FiTruck aria-hidden='true' />Delivery &amp; setup required</span>}
        {product.outdoor_rated && <span className={styles.weatherproof}>Weatherproof</span>}
      </div>
      <button className={styles.productName} onClick={onDetails} type='button'>{product.name}</button>
      <p>{helper.shortText(product.description || '', 24)}</p>
      {specifications.length > 0 && <ul className={styles.cardSpecs} aria-label={`${product.name} key details`}>
        {specifications.map(specification => <li key={`${specification.label}-${specification.value}`}><span>{specification.label}</span><strong>{specification.value}</strong></li>)}
      </ul>}
      {includedCount > 0 && <span className={styles.includes}><FiPackage aria-hidden='true' />Includes {includedCount} {includedCount === 1 ? 'accessory' : 'accessories'} at no extra charge</span>}
      <div className={styles.productCardFooter}>
        <div className={styles.cardPrice}><span>{product.only_purchase ? 'Purchase price' : 'Rental price'}</span><strong>{helper.formatPrice(product.only_purchase ? (product?.price?.map || product?.price?.purchase || 0) : (product?.price?.rental || 0))}{!product.only_purchase && <small> / day</small>}</strong></div>
        <div className={styles.cardAction}>
          {publicActions || <AddUnits
              actionIcon={deliveryOnly ? FiTruck : undefined}
              actionLabel={deliveryOnly ? 'Add with delivery' : undefined}
              actionVariant={deliveryOnly ? 'outline' : 'primary'}
              busy={locked}
              contextName={contextName}
              disabled={availability.unavailable}
              disabledLabel='Unavailable'
              onAdd={onAdd}
              pack={pack}
              product={product}
              selectedUnits={selectedUnits}
              ui='v2'
            />}
        </div>
      </div>
    </div>
  </article>
}

const ProductionEquipmentSelector = ({ mode = 'production', onOpenPremadePackages, premadePackageLabel = 'Add premade package' }) => {
  const dispatch = useDispatch()
  const router = useRouter()
  const productSlice = useSelector(getProductSlice)
  const filters = useSelector(getProductsFilters)
  const allProducts = useSelector(getAllFilteredProducts)
  const order = useSelector(getWorkingOrder)
  const { productionSetupDrawerOpen = false } = useSelector(getWorkingOrderSettings)
  const { cart, user } = useSelector(getUser)
  const geo = useSelector(getUserGeo)
  const pack = useSelector(getWorkingPackage)
  const persistencePending = useSelector(getWorkingPackagePersistencePending)
  const rentalMode = mode === 'rental'
  const browseMode = mode === 'browse'
  const { launchIntent, unifiedEnabled } = useOrderFlow()
  const shoppingMarket = resolveShoppingMarket({ geo, order, pathname: router.pathname })
  const browsingOrlando = shoppingMarket === 'Orlando'
  const unifiedEligible = browseMode && unifiedEnabled && !pack && !order?._id && !cart?.items?.length
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState('recommended')
  const [viewMode, setViewMode] = useState('cards')
  const [availableOnly, setAvailableOnly] = useState(false)
  const [pickupOnly, setPickupOnly] = useState(false)
  const [bundleOnly, setBundleOnly] = useState(false)
  const [singleOnly, setSingleOnly] = useState(false)
  const [visibleCount, setVisibleCount] = useState(24)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [detailsProduct, setDetailsProduct] = useState(null)
  const [toast, setToast] = useState(null)
  const [isPhone, setIsPhone] = useState(false)
  const [pendingChange, setPendingChange] = useState(null)
  const [reviewingRental, setReviewingRental] = useState(false)
  const searchId = useId()
  const setupName = rentalMode ? 'your rental' : browseMode ? 'your order' : (pack?.name || 'Setup')
  const mutationPending = rentalMode ? reviewingRental : Boolean((!browseMode || pack) && (pendingChange || persistencePending))
  const drawerOpen = Boolean(!rentalMode && !browseMode && productionSetupDrawerOpen)
  const availabilityChecked = Boolean(!browseMode && productSlice?.geoProducts?.availableChecked)
  const inventoryLoading = Boolean(browseMode ? productSlice?.loading : productSlice?.geoProducts?.loading)
  const selectedSubcategories = useMemo(
    () => Object.keys(filters?.categories || {}).filter(key => filters.categories[key]),
    [filters?.categories]
  )
  const selectedSubcategoryKey = selectedSubcategories.join('|')

  useEffect(() => {
    if (!drawerOpen) return undefined

    document.body.classList.add('msrProductionSetupDrawerOpen')
    return () => document.body.classList.remove('msrProductionSetupDrawerOpen')
  }, [drawerOpen])

  const catalogProducts = useMemo(() => {
    const source = browseMode
      ? Object.values(productSlice?.byId || {})
      : productSlice?.showGeo
      ? (productSlice?.geoProducts?.list || [])
      : Object.values(productSlice?.byId || {})

    return source.filter(product => {
      if (product?.status !== 'active') return false
      if (rentalMode && (product?.only_purchase || product?.price?.map)) return false
      if (browseMode) return browsingOrlando || !product?.only_orlando
      if (!filters?.miami) return Boolean(filters?.orlando && product?.orlando)
      return true
    })
  }, [browseMode, browsingOrlando, productSlice?.showGeo, productSlice?.geoProducts?.list, productSlice?.byId, filters?.miami, filters?.orlando, rentalMode])

  const productById = useMemo(() => {
    const lookup = new Map()
    catalogProducts.forEach(product => lookup.set(String(product?._id), product))
    Object.values(productSlice?.byId || {}).forEach(product => {
      if (!lookup.has(String(product?._id))) lookup.set(String(product?._id), product)
    })
    return lookup
  }, [catalogProducts, productSlice?.byId])

  const resolveProduct = useCallback(value => {
    if (value?.name) return productById.get(String(value?._id)) || value
    return productById.get(String(value))
  }, [productById])

  const isPickupEligible = useCallback(product => Boolean(
    product?.allow_pickup
    && !product?.requires_tech
    && (!(browseMode ? browsingOrlando : order?.orlando) || product?.orlando)
  ), [browseMode, browsingOrlando, order?.orlando])

  const getAvailability = useCallback(product => {
    const atOrlando = browseMode ? browsingOrlando : Boolean(order?.orlando)
    if (!availabilityChecked) return { checked: false, count: null, unavailable: false, low: false, orlando: atOrlando, shipsFromMiami: Boolean(atOrlando && !product?.orlando) }

    const counts = []
    const productCount = Number(product?.available)
    if (Number.isFinite(productCount)) counts.push(productCount)
    ;(product?.included || []).forEach(included => {
      const includedProduct = resolveProduct(included?.product)
      const includedAvailable = Number(includedProduct?.available)
      const units = Math.max(1, Number(included?.units) || 1)
      if (Number.isFinite(includedAvailable)) counts.push(Math.floor(includedAvailable / units))
    })
    const count = counts.length ? Math.max(0, Math.min(...counts)) : null
    const unavailable = count === 0
    const totalItems = Number(product?.total_items)
    const low = count != null && count > 0 && (count <= 3 || (Number.isFinite(totalItems) && totalItems > 0 && count / totalItems <= 0.4))

    return { checked: true, count, unavailable, low, orlando: atOrlando, shipsFromMiami: Boolean(atOrlando && !product?.orlando) }
  }, [availabilityChecked, browseMode, browsingOrlando, order?.orlando, resolveProduct])

  const selectedCategory = useMemo(() => {
    if (!filters?.mainCat) return null
    return PRODUCTION_EQUIPMENT_CATEGORIES.find(category => tag(category) === tag(filters.mainCat)) || helper.tagUpper(filters.mainCat)
  }, [filters?.mainCat])

  const categoryCounts = useMemo(() => PRODUCTION_EQUIPMENT_CATEGORIES.reduce((counts, category) => ({
    ...counts,
    [category]: catalogProducts.filter(product => productMatchesCategory(product, category)).length
  }), {}), [catalogProducts])

  const subcategories = useMemo(() => {
    if (!selectedCategory) return []
    const values = new Set()
    catalogProducts.filter(product => productMatchesCategory(product, selectedCategory)).forEach(product => {
      ;(product?.categories || []).forEach(path => {
        const [, subcategory] = String(path).split('/')
        if (subcategory && (getProductionEquipmentCategory({ categories: [path] }) === selectedCategory || selectedCategory === 'Other')) values.add(subcategory)
      })
    })
    return [...values].sort((a, b) => a.localeCompare(b))
  }, [catalogProducts, selectedCategory])

  const subcategoryCounts = useMemo(() => subcategories.reduce((counts, subcategory) => ({
    ...counts,
    [subcategory]: catalogProducts.filter(product => productMatchesCategory(product, selectedCategory) && productMatchesSubcategories(product, [tag(subcategory)])).length
  }), {}), [catalogProducts, selectedCategory, subcategories])

  const shownProducts = useMemo(() => {
    const search = normalize(query)
    let result = catalogProducts.filter(product => {
      if (search) {
        const searchable = [product.name, product.brand, product.model, product.description, ...(product.categories || []), ...(product.snippets || [])].join(' ').toLowerCase()
        if (!searchable.includes(search)) return false
      } else {
        if (!selectedCategory || !productMatchesCategory(product, selectedCategory)) return false
        if (!productMatchesSubcategories(product, selectedSubcategories)) return false
      }

      if (bundleOnly && !singleOnly && !product?.bundle) return false
      if (singleOnly && !bundleOnly && product?.bundle) return false
      if (availableOnly && getAvailability(product).unavailable) return false
      if (pickupOnly && !isPickupEligible(product)) return false
      return true
    })

    result = [...result].sort((a, b) => {
      if (sort === 'price-low') return (Number(a?.price?.rental) || 0) - (Number(b?.price?.rental) || 0)
      if (sort === 'price-high') return (Number(b?.price?.rental) || 0) - (Number(a?.price?.rental) || 0)
      if (sort === 'availability') {
        const aCount = getAvailability(a).count
        const bCount = getAvailability(b).count
        return (bCount == null ? -1 : bCount) - (aCount == null ? -1 : aCount)
      }
      return ((Number(b?.marketing_priority) || 1) - (Number(a?.marketing_priority) || 1)) || String(a?.name || '').localeCompare(String(b?.name || ''))
    })

    return result
  }, [catalogProducts, query, selectedCategory, selectedSubcategories, bundleOnly, singleOnly, availableOnly, pickupOnly, sort, getAvailability, isPickupEligible])
  const visibleProducts = shownProducts.slice(0, visibleCount)

  const standaloneComponents = rentalMode || (browseMode && !pack)
    ? (cart?.items || []).map(item => ({ ...item, product: item?.product }))
    : (pack?.components || []).filter(component => component?.builder_index == null)
  const selectedUnitCount = standaloneComponents.reduce((total, component) => total + (Number(component?.units) || 0), 0)
  const configuredSystemCount = rentalMode || (browseMode && !pack) ? 0 : (pack?.builders || []).length
  const compactSetupCountLabel = [
    selectedUnitCount > 0 ? `${selectedUnitCount} ${selectedUnitCount === 1 ? 'item' : 'items'}` : '',
    configuredSystemCount > 0 ? `${configuredSystemCount} ${configuredSystemCount === 1 ? 'system' : 'systems'}` : '',
  ].filter(Boolean).join(' + ') || 'empty'
  const setupSubtotal = rentalMode
    ? standaloneComponents.reduce((total, component) => total + ((Number(component?.product?.price?.rental) || 0) * (Number(component?.units) || 0) * (Number(order?.rental?.total_days) || 1)), 0)
    : (Number(pack?.price) || 0) + (Number(pack?.purchase_price) || 0) + ((Number(pack?.production_price) || 0) * 1.35)
  const hasSelection = Boolean(standaloneComponents.length || (!rentalMode && pack?.builders?.length))
  const activeFilterCount = selectedSubcategories.length + Number(bundleOnly) + Number(singleOnly) + Number(!browseMode && availableOnly) + Number(!browseMode && pickupOnly)
  const selectedUnitsFor = product => (
    standaloneComponents.find(component => String(component?.product?._id || component?.product) === String(product?._id))?.units || 0
  )

  useEffect(() => {
    const media = window.matchMedia('(max-width: 599px)')
    const update = () => setIsPhone(media.matches)
    update()
    media.addEventListener?.('change', update)
    return () => media.removeEventListener?.('change', update)
  }, [])

  useEffect(() => {
    if (!toast) return undefined
    const timer = window.setTimeout(() => setToast(null), 3500)
    return () => window.clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [])

  useEffect(() => {
    if (!browseMode) return undefined
    const resetCatalogFilters = () => dispatch(productsSetFilters({ bundle: false, categories: {}, mainCat: null, single: false }))
    resetCatalogFilters()
    return resetCatalogFilters
  }, [browseMode, dispatch])

  useEffect(() => () => {
    if (!rentalMode && !browseMode) dispatch(workingOrderSetSettings({ productionSetupDrawerOpen: false }))
  }, [browseMode, dispatch, rentalMode])

  useEffect(() => {
    setVisibleCount(24)
  }, [query, selectedCategory, selectedSubcategoryKey, bundleOnly, singleOnly, availableOnly, pickupOnly, sort])

  useEffect(() => {
    const search = query.trim()
    if (search.length < 2) return undefined
    const timer = window.setTimeout(() => {
      gtag.event({ action: 'production_selector_search', label: 'leads', value: shownProducts.length, category: search })
    }, 700)
    return () => window.clearTimeout(timer)
  }, [query, shownProducts.length])

  const selectCategory = category => {
    setQuery('')
    setSort('recommended')
    dispatch(productsSetFilters({ mainCat: tag(category), categories: {} }))
    gtag.event({ action: 'package_products_category_click', label: 'leads', value: 0, category })
  }

  const clearFilters = () => {
    dispatch(productsSetFilters({ categories: {}, bundle: false, single: false }))
    setBundleOnly(false)
    setSingleOnly(false)
    setAvailableOnly(false)
    setPickupOnly(false)
    gtag.event({ action: 'production_selector_clear_filters', label: 'leads', value: 0, category: 'orders' })
  }

  const toggleSubcategory = subcategory => {
    const key = tag(subcategory)
    const next = { ...(filters?.categories || {}), [key]: !filters?.categories?.[key] }
    dispatch(productsSetFilters({ categories: next }))
    gtag.event({ action: 'production_selector_filter', label: 'leads', value: 0, category: subcategory })
  }

  const loadOrderProduct = async (requestedProduct, showSpinner = true) => {
    let loadedProducts
    if (filters?.static) {
      loadedProducts = await dispatch(loadEntityForce('products', null, null, showSpinner))
      dispatch(productsResetFilters())
      if (browseMode) {
        dispatch(productsSetFilters({
          categories: filters?.categories || {},
          mainCat: filters?.mainCat || null
        }))
      }
    }

    const currentProduct = resolveOrderProduct({
      requestedProduct,
      loadedProducts,
      cachedProducts: allProducts
    })

    return {
      currentProduct,
      orderProducts: Array.isArray(loadedProducts) ? loadedProducts : allProducts
    }
  }

  const addCatalogProductToOrder = async (product, delivery) => {
    const units = 1

    if (unifiedEligible && !product.only_purchase && !product?.price?.map) {
      return launchIntent({
        kind: 'product',
        entityId: product._id,
        entityName: product.name,
        sourceSurface: 'all_equipment_catalog',
        fulfillment: delivery ? 'delivery_setup' : 'pickup'
      })
    }

    if (order?.request_type === 'purchase' && !product?.price?.map) {
      if (!window.confirm('This product is only available for rent. Do you want to create a new rental order?')) return
      return dispatch(apiCallBegan({
        url: `/order/${order._id}`,
        method: 'DELETE',
        onSuccess: workingOrderReset.type,
        onSuccessFunc: () => cartCleaned()
      }))
    }

    const { currentProduct, orderProducts } = await loadOrderProduct(product)
    if (!currentProduct) {
      return dispatch(showAlert({
        type: 'error',
        alert: `${product.name} could not be loaded. Please refresh and try again.`
      }))
    }

    if (delivery) {
      if (browsingOrlando && requiresMiamiFulfillment(currentProduct, units)) {
        if (!window.confirm(`${currentProduct.name} is available from our Miami warehouse, not the Orlando warehouse, for these dates. Continuing will create or convert the order to Miami fulfillment and may add a travel fee. Continue?`)) return
      }

      if (!cart?.production && order?._id) {
        await dispatch(apiCallBegan({
          url: `/order/${order._id}`,
          method: 'DELETE',
          onSuccess: workingOrderReset.type,
          showSpinner: true
        }))
      }

      return dispatch(cartAddItem({
        product: currentProduct,
        units,
        category: product?.categories?.[0]?.split('/')?.[1],
        delivery: true
      }))
    }

    const bundleUnavailable = currentProduct.bundle && currentProduct.included?.some(item => (
      orderProducts.find(candidate => String(candidate._id) === String(item?.product?._id || item?.product))?.available
      - (units * item.units) < 0
    ))
    if (order?._id && units > 0 && (currentProduct.available - units < 0 || bundleUnavailable)) {
      return dispatch(showAlert({
        type: 'error',
        alert: `${product.name} has no more units available for the selected date`
      }))
    }

    if (!cart?.production && (product.requires_tech || !product.allow_pickup)) {
      if (!window.confirm(`${product.name} requires installation by a Miami Sound Rental technician. Continue?`)) return
      if (order?._id) {
        await dispatch(apiCallBegan({
          url: `/order/${order._id}`,
          method: 'DELETE',
          onSuccess: workingOrderReset.type
        }))
      }
    }

    return dispatch(cartAddItem({
      product: currentProduct,
      units,
      category: product?.categories?.[0]?.split('/')?.[1]
    }))
  }

  const openCatalogRentalStart = (product, trackingContext = {}) => {
    setDetailsProduct(null)
    if (order?._id && order?.request_type === 'rental' && !cart?.production) {
      return addCatalogProductToOrder(product)
    }

    gtag.event({
      action: 'rent_online_cta_click',
      label: 'sales',
      value: 5,
      category: 'orders',
      sourceSurface: 'all_equipment_catalog',
      ...trackingContext
    })

    dispatch(SHOW_POPUP({
      key: `rentalStart-${product._id}`,
      size: 'xl',
      className: 'rentalStartShell',
      trapFocus: true,
      ariaLabelledBy: 'rental-start-title',
      body: <RentalStartPopup
        product={product}
        orlando={browsingOrlando}
        selectionOnly={unifiedEligible}
        resolveProduct={unifiedEligible ? undefined : async () => {
          const { currentProduct } = await loadOrderProduct(product, false)
          if (!currentProduct) {
            dispatch(showAlert({
              type: 'error',
              alert: `${product.name} could not be loaded. Please refresh and try again.`
            }))
            return null
          }
          return currentProduct
        }}
        onSelect={async (delivery, selectedProduct) => {
          if (unifiedEligible) {
            dispatch(CLOSE_POPUP({ all: true }))
            await addCatalogProductToOrder(selectedProduct, delivery)
            return false
          }
          if (delivery && browsingOrlando && requiresMiamiFulfillment(selectedProduct)) {
            if (!window.confirm(`${selectedProduct.name} is available from our Miami warehouse, not the Orlando warehouse. Continuing will create a Miami-fulfilled order and may add a travel fee. Continue?`)) return false
          }
          return true
        }}
      />
    }))
  }

  const handleAdd = result => {
    const units = Number(result?.units) || 0
    const message = units > 0 ? `${result?.product?.name || 'Equipment'} added to ${setupName}` : `${result?.product?.name || 'Equipment'} updated`
    if (rentalMode) {
      setToast({ product: result?.product, message })
      gtag.event({ action: 'rental_selector_item_updated', label: 'leads', value: units, category: 'orders' })
      return
    }
    setPendingChange({ expectedUnits: Number(result?.selectedUnits) || 0, message, mutationId: result?.mutationId, product: result?.product })
    gtag.event({ action: 'production_selector_item_updated', label: 'leads', value: units, category: 'orders' })
  }

  useEffect(() => {
    if (rentalMode || !pendingChange?.product || persistencePending) return undefined
    const savedUnits = (pack?.components || [])
      .filter(component => component?.builder_index == null)
      .find(component => String(component?.product?._id || component?.product) === String(pendingChange.product?._id))?.units || 0
    if (Number(savedUnits) !== Number(pendingChange.expectedUnits)) return undefined
    const timer = window.setTimeout(() => {
      setToast({ product: pendingChange.product, message: pendingChange.message })
      setPendingChange(null)
    }, 75)
    return () => window.clearTimeout(timer)
  }, [pack?.components, pendingChange, persistencePending, rentalMode])

  useEffect(() => {
    if (rentalMode || !pendingChange?.product) return undefined
    const timer = window.setTimeout(() => {
      setToast({
        product: pendingChange.product,
        message: 'We could not confirm that change. Check the setup quantity, then try again.'
      })
      setPendingChange(null)
    }, 4000)
    return () => window.clearTimeout(timer)
  }, [pendingChange, rentalMode])

  const openDrawer = source => {
    if (rentalMode) {
      dispatch(workingOrderSetSettings({ openRentalCart: true }))
      gtag.event({ action: 'rental_selector_open_cart', label: 'leads', value: 0, category: source })
      return
    }
    dispatch(workingOrderSetSettings({ productionSetupDrawerOpen: true }))
    gtag.event({ action: 'production_selector_open_setup', label: 'leads', value: 0, category: source })
  }

  const reviewSetup = async () => {
    if (rentalMode) {
      if (!order?._id || reviewingRental) return
      setReviewingRental(true)
      try {
        const finalized = await dispatch(finalizeRentalDraft(order._id))
        if (!finalized) return
        await router.push(`/planner?id=${order._id}`)
      } finally {
        setReviewingRental(false)
      }
      return
    }
    gtag.event({ action: 'package_products_go_to_builder', label: 'leads', value: 0, category: 'orders' })
    dispatch(workingOrderSetSettings({ tutorial: 1 }))
    dispatch(packageViewSet('builder'))
    dispatch(openSideMenu(false))
  }

  const openBuilder = videowall => {
    gtag.event({ action: videowall ? 'production_selector_open_video_wall_builder' : 'production_selector_open_stage_builder', label: 'leads', value: 0, category: 'orders' })
    dispatch(SHOW_POPUP({
      size: 'xxl',
      key: 'stageBuilder',
      body: <Suspense fallback={<Spinner size='tiny' />}>{videowall ? <VideoWallBuilder /> : <StageBuilder />}</Suspense>
    }))
  }

  const clearSearch = () => setQuery('')

  const changeViewMode = mode => {
    setViewMode(mode)
    gtag.event({ action: 'production_selector_view_change', label: 'leads', value: 0, category: mode })
  }

  return <UiBoundary aria-busy={inventoryLoading} className={styles.root} data-mobile-saving={Boolean(isPhone && mutationPending)} data-msr-production-selector='true' data-msr-public-catalog={browseMode ? 'true' : undefined}>
    <header className={styles.pageHeader}>
      <div className={styles.headerCopy}>
        <span className={styles.eyebrow}>{browseMode ? 'Complete rental equipment catalog' : rentalMode ? 'Rental equipment browser' : 'Package Builder · Add equipment'}</span>
        <h1>{browseMode ? 'Explore all rental equipment' : `Add equipment to ${setupName}`}</h1>
        <p>{browseMode
          ? 'Search every equipment category in one place. Choose dates from any product when you are ready to start an order.'
          : `Search the full catalog or browse by category. Availability is based on this ${rentalMode ? 'rental’s dates and warehouse' : 'production’s date and location'}.`}</p>
      </div>
      {!browseMode && <div className={styles.headerActions}>
        <Action className={`${styles.headerAction} ${styles.headerSummaryAction}`} icon={FiGrid} onClick={() => openDrawer('header')} variant='secondary'>
          <span className={styles.summaryAction}><strong>{compactSetupCountLabel}</strong><small>{helper.formatPrice(setupSubtotal)} {rentalMode ? 'equipment subtotal' : 'estimated setup'}</small></span>
        </Action>
        <Action className={`${styles.headerAction} ${styles.headerReviewAction}`} disabled={!hasSelection || mutationPending} icon={FiArrowRight} onClick={reviewSetup}>{mutationPending ? (rentalMode ? 'Preparing rental…' : 'Saving setup…') : (rentalMode ? 'Review rental' : 'Review setup')}</Action>
      </div>}
    </header>

    <section aria-labelledby={`${searchId}-label`} className={styles.searchPanel}>
      <div className={styles.searchCopy}>
        <label id={`${searchId}-label`} htmlFor={searchId}>{browseMode ? 'Search all rental equipment' : `Search equipment for ${rentalMode ? 'your rental' : 'this setup'}`}</label>
        <span>{browseMode
          ? 'Search by product, feature or use case, or browse the categories below.'
          : rentalMode
            ? 'Add and compare equipment without leaving this browser. Your rental stays available in the cart.'
            : `Results stay inside ${setupName}; adding an item will not take you away from the Package Builder.`}</span>
      </div>
      <div className={styles.searchControl}>
        <FiSearch aria-hidden='true' />
        <input
          id={searchId}
          disabled={inventoryLoading || mutationPending}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Escape') clearSearch()
          }}
          placeholder='Search speakers, projectors, stages, lighting…'
          type='search'
          value={query}
        />
        {query && <button aria-label='Clear equipment search' onClick={clearSearch} type='button'><FiX aria-hidden='true' /></button>}
      </div>
    </section>

    <nav aria-label='Equipment categories' className={`${styles.categories} ${selectedCategory ? styles.categoriesCompact : ''}`.trim()}>
      {PRODUCTION_EQUIPMENT_CATEGORIES.map(category => {
        const CategoryIcon = categoryIcons[category] || FiPackage
        const selected = !query && selectedCategory === category
        return <button aria-pressed={selected} className={styles.category} disabled={inventoryLoading || mutationPending} key={category} onClick={() => selectCategory(category)} type='button'>
          <CategoryIcon aria-hidden='true' />
          <span><strong>{category}</strong><small>{categoryCounts[category] || 0} products</small></span>
        </button>
      })}
    </nav>

    {inventoryLoading ? <section aria-live='polite' className={styles.inventoryLoading} role='status'>
      <Spinner size='small' />
      <div>
        <h2>Loading equipment inventory</h2>
        <p>{browseMode ? 'Preparing the complete equipment catalog…' : 'Checking availability for this order’s dates and location…'}</p>
      </div>
    </section> : !selectedCategory && !query ? <section className={styles.startPanel}>
      <FiGrid aria-hidden='true' />
      <h2>Choose a category or search the full catalog</h2>
      <p>{browseMode ? 'Explore sound, staging, visuals, DJ equipment, lighting, special effects and more from one catalog.' : `You can return here at any time. Your ${rentalMode ? 'rental cart' : 'setup summary'} stays available while you browse.`}</p>
      {onOpenPremadePackages && <Action icon={FiPackage} onClick={onOpenPremadePackages} variant='secondary'>{premadePackageLabel}</Action>}
    </section> : <>
      {!rentalMode && (selectedCategory === 'Staging' || selectedCategory === 'Visuals') && !query && <section className={styles.builderPromo}>
        <span className={styles.builderIcon}>{selectedCategory === 'Visuals' ? <FiMonitor aria-hidden='true' /> : <FiLayers aria-hidden='true' />}</span>
        <div>
          <strong>Need a custom {selectedCategory === 'Visuals' ? 'LED video wall' : 'stage'}?</strong>
          <span>{browseMode
            ? 'Choose the dimensions and options, then select dates to start your order.'
            : 'Enter dimensions once and the builder will add the correct system components to this setup.'}</span>
        </div>
        <Action icon={FiSliders} onClick={() => openBuilder(selectedCategory === 'Visuals')}>
          {selectedCategory === 'Visuals' ? 'Configure Video Wall' : 'Configure Stage'}
        </Action>
      </section>}

      <div className={`${styles.catalogLayout} ${query ? styles.catalogLayoutSearch : ''}`.trim()}>
        {!query && <aside className={`${styles.filters} ${filtersOpen ? styles.filtersOpen : ''}`.trim()} aria-label='Product filters'>
          <div className={styles.filterHeader}>
            <div><FiFilter aria-hidden='true' /><strong>Filters</strong>{activeFilterCount > 0 && <span>{activeFilterCount}</span>}</div>
            {activeFilterCount > 0 && <button onClick={clearFilters} type='button'>Clear all</button>}
          </div>
          <fieldset>
            <legend>{selectedCategory} categories</legend>
            {subcategories.map(subcategory => {
              const key = tag(subcategory)
              return <label key={subcategory}>
                <input checked={Boolean(filters?.categories?.[key])} onChange={() => toggleSubcategory(subcategory)} type='checkbox' />
                <span>{subcategory}</span><small>{subcategoryCounts[subcategory] || 0}</small>
              </label>
            })}
          </fieldset>
          <fieldset>
            <legend>Product type</legend>
            <label><input checked={bundleOnly} onChange={event => {
              setBundleOnly(event.target.checked)
              gtag.event({ action: 'production_selector_filter', label: 'leads', value: Number(event.target.checked), category: 'bundles' })
            }} type='checkbox' /><span>Bundles</span></label>
            <label><input checked={singleOnly} onChange={event => {
              setSingleOnly(event.target.checked)
              gtag.event({ action: 'production_selector_filter', label: 'leads', value: Number(event.target.checked), category: 'single_items' })
            }} type='checkbox' /><span>Single items</span></label>
          </fieldset>
          {!browseMode && <fieldset>
            <legend>Availability</legend>
            <label><input checked={availableOnly} onChange={event => {
              setAvailableOnly(event.target.checked)
              gtag.event({ action: 'production_selector_filter', label: 'leads', value: Number(event.target.checked), category: 'available_for_event' })
            }} type='checkbox' /><span>Available for my event</span></label>
            {rentalMode && <label><input checked={pickupOnly} onChange={event => {
              setPickupOnly(event.target.checked)
              gtag.event({ action: 'rental_selector_filter', label: 'leads', value: Number(event.target.checked), category: 'warehouse_pickup' })
            }} type='checkbox' /><span>Available for warehouse pickup</span></label>}
          </fieldset>}
        </aside>}

        <section className={styles.results} aria-label='Equipment results'>
          <div className={styles.resultsToolbar}>
            <div>
              <h2>{query ? `Search results for “${query}”` : selectedCategory}</h2>
              <span>{shownProducts.length > visibleCount ? `Showing ${visibleProducts.length} of ${shownProducts.length} products` : `${shownProducts.length} ${shownProducts.length === 1 ? 'product' : 'products'}`}</span>
            </div>
            <div className={styles.toolbarActions}>
              {!query && <Action ariaExpanded={filtersOpen} className={styles.mobileFilter} icon={FiFilter} onClick={() => setFiltersOpen(value => !value)} size='compact' variant='secondary'>Filters {activeFilterCount > 0 && `(${activeFilterCount})`}</Action>}
              <div aria-label='Results view' className={styles.viewToggle} role='group'>
                <button aria-label='Card view' aria-pressed={viewMode === 'cards'} onClick={() => changeViewMode('cards')} type='button'>
                  <FiGrid aria-hidden='true' /><span>Cards</span>
                </button>
                <button aria-label='List view' aria-pressed={viewMode === 'list'} onClick={() => changeViewMode('list')} type='button'>
                  <FiList aria-hidden='true' /><span>List</span>
                </button>
              </div>
              <label className={styles.sortControl}><span>Sort by</span><select onChange={event => {
                setSort(event.target.value)
                gtag.event({ action: 'production_selector_sort', label: 'leads', value: 0, category: event.target.value })
              }} value={sort}>
                <option value='recommended'>Best match</option>
                {!browseMode && <option value='availability'>Availability</option>}
                <option value='price-low'>Lowest price</option>
                <option value='price-high'>Highest price</option>
              </select></label>
            </div>
          </div>

          {!query && activeFilterCount > 0 && <div className={styles.filterChips} aria-label='Active filters'>
            {selectedSubcategories.map(subcategory => <button key={subcategory} onClick={() => toggleSubcategory(subcategory)} type='button'>{helper.tagUpper(subcategory)}<FiX aria-hidden='true' /></button>)}
            {bundleOnly && <button onClick={() => setBundleOnly(false)} type='button'>Bundles<FiX aria-hidden='true' /></button>}
            {singleOnly && <button onClick={() => setSingleOnly(false)} type='button'>Single items<FiX aria-hidden='true' /></button>}
            {availableOnly && <button onClick={() => setAvailableOnly(false)} type='button'>Available for my event<FiX aria-hidden='true' /></button>}
            {pickupOnly && <button onClick={() => setPickupOnly(false)} type='button'>Warehouse pickup<FiX aria-hidden='true' /></button>}
          </div>}

          {shownProducts.length ? <div className={`${styles.productGrid} ${viewMode === 'list' ? styles.productList : ''}`.trim()}>
            {visibleProducts.map(product => <ProductCard
              availability={getAvailability(product)}
              contextName={rentalMode ? 'rental' : 'setup'}
              deliveryOnly={Boolean(rentalMode && !isPickupEligible(product))}
              key={product._id}
              locked={mutationPending}
              onAdd={handleAdd}
              onDetails={() => {
                setDetailsProduct(product)
                gtag.event({ action: 'product_miniature_view_details', label: 'leads', value: 0, category: 'products' })
              }}
              pack={rentalMode ? undefined : pack}
              product={product}
              publicActions={browseMode ? <RentalCatalogActions
                addToCartItems={addCatalogProductToOrder}
                card
                cart={cart}
                locked={mutationPending}
                onAdd={handleAdd}
                openRentalStart={openCatalogRentalStart}
                order={order}
                orlando={browsingOrlando}
                pack={pack}
                product={product}
                selectedUnits={selectedUnitsFor(product)}
                user={user}
              /> : null}
              selectedUnits={selectedUnitsFor(product)}
            />)}
          </div> : <div className={styles.noResults}>
            <FiSearch aria-hidden='true' />
            <h3>No equipment matches these choices</h3>
            <p>Clear a filter or try a broader search. Your current {rentalMode ? 'rental' : 'setup'} has not changed.</p>
            <Action onClick={() => { clearFilters(); clearSearch() }} variant='secondary'>Clear filters and search</Action>
          </div>}
          {visibleCount < shownProducts.length && <div className={styles.loadMore}>
            <Action onClick={() => setVisibleCount(count => count + 24)} variant='secondary'>Show 24 more products</Action>
            <span>{shownProducts.length - visibleCount} remaining</span>
          </div>}
        </section>
      </div>
    </>}

    {!browseMode && <div className={styles.mobileSummary}>
      <Action disabled={mutationPending} icon={FiGrid} onClick={() => openDrawer('mobile-summary')} variant='secondary'>{mutationPending ? (rentalMode ? 'Updating rental…' : 'Saving setup…') : `View ${rentalMode ? 'rental' : 'setup'} · ${compactSetupCountLabel}`}</Action>
      <Action disabled={!hasSelection || mutationPending} icon={FiArrowRight} onClick={reviewSetup}>{mutationPending ? 'Please wait' : `Review ${rentalMode ? 'rental' : 'setup'}`}</Action>
    </div>}

    {mutationPending && <div aria-live='polite' className={`${styles.toast} ${styles.savingToast}`} role='status'><span className={styles.savingDot} aria-hidden='true' /><span>{rentalMode ? 'Preparing your rental for review…' : `Saving ${pendingChange?.product?.name || 'equipment'} to ${setupName}…`}</span></div>}
    {toast && <div aria-live='polite' className={styles.toast} role='status'>
      <FiCheckCircle aria-hidden='true' /><span>{toast.message}</span>{!browseMode && <button onClick={() => openDrawer('add-confirmation')} type='button'>View {rentalMode ? 'rental' : 'setup'}</button>}
    </div>}

    {!rentalMode && !browseMode && <SetupDrawer getAvailability={getAvailability} isPhone={isPhone} locked={mutationPending} open={drawerOpen} pack={pack} resolveProduct={resolveProduct} onAdd={handleAdd} onClose={() => dispatch(workingOrderSetSettings({ productionSetupDrawerOpen: false }))} onReview={reviewSetup} />}
    <ProductDetailsDialog
      availability={detailsProduct ? getAvailability(detailsProduct) : { checked: false }}
      contextName={rentalMode ? 'rental' : 'setup'}
      deliveryOnly={Boolean(rentalMode && detailsProduct && !isPickupEligible(detailsProduct))}
      locked={mutationPending}
      onAdd={handleAdd}
      onClose={() => setDetailsProduct(null)}
      pack={rentalMode ? undefined : pack}
      product={detailsProduct}
      publicActions={browseMode && detailsProduct ? <RentalCatalogActions
        addToCartItems={addCatalogProductToOrder}
        cart={cart}
        locked={mutationPending}
        onAdd={handleAdd}
        openRentalStart={openCatalogRentalStart}
        order={order}
        orlando={browsingOrlando}
        pack={pack}
        product={detailsProduct}
        selectedUnits={selectedUnitsFor(detailsProduct)}
        user={user}
      /> : null}
      resolveProduct={resolveProduct}
      selectedUnits={detailsProduct ? selectedUnitsFor(detailsProduct) : 0}
    />
  </UiBoundary>
}

export default ProductionEquipmentSelector
